// @oasis-permission-inherits: /jerboa-client-network-load (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { AssetTable } from "~/modules/jerboa-client-network-load/components/asset-table";
import {
  formatBytes,
  formatDurationMs,
  formatMbps,
  formatTimestamp,
  formatYesNo,
  statusVariant,
} from "~/modules/jerboa-client-network-load/formatters";
import { getJerboaClientNetworkLoadQuery } from "~/modules/jerboa-client-network-load/queries";
import {
  jerboaClientNetworkLoadContextSchema,
  jerboaClientNetworkLoadStatusSchema,
} from "~/modules/jerboa-client-network-load/schemas";
import { JerboaUserEnvCard } from "~/modules/jerboa/components/user-env-card";

export const Route = createFileRoute("/_dash/jerboa-client-network-load/$id")({
  validateSearch: z.object({
    page: z.number().optional(),
    status: jerboaClientNetworkLoadStatusSchema.optional(),
    context: jerboaClientNetworkLoadContextSchema.optional(),
    projectId: z.string().optional(),
    metricDays: z.union([z.literal(1), z.literal(7), z.literal(30)]).optional(),
  }),
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;
    await queryClient.prefetchQuery(
      getJerboaClientNetworkLoadQuery.queryOptions({
        pathParams: { id: params.id },
      })
    );
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  const { page, status, context, projectId, metricDays } = Route.useSearch();
  return (
    <div className="space-y-6">
      <Link
        to="/jerboa-client-network-load"
        search={{ page, status, context, projectId, metricDays }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Network Load Results
      </Link>
      <Skeleton className="h-10 w-96 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium">{value ?? "-"}</div>
    </div>
  );
}

function RouteComponent() {
  const { id } = Route.useParams();
  const { page, status, context, projectId, metricDays } = Route.useSearch();

  const { data } = getJerboaClientNetworkLoadQuery.useSuspenseQuery({
    pathParams: { id },
  });

  const result = data.result;
  const totalBytes = result.assetBytesFromNetwork + result.assetBytesFromCache;

  return (
    <div className="space-y-6">
      <Link
        to="/jerboa-client-network-load"
        search={{ page, status, context, projectId, metricDays }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Network Load Results
      </Link>

      <JerboaUserEnvCard
        id={result.id}
        captionsProjectId={result.captionsProjectId}
        projectProtoUri={result.projectProtoUri}
        sessionId={result.sessionId}
        userId={result.userId}
        orgId={result.orgId}
        browserInfo={result.browserInfo}
      />

      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Badge variant={statusVariant(result.status)}>{result.status}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Context:</span>
            <Badge variant="outline">{result.context}</Badge>
          </div>
          {result.editType && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Edit Type:</span>
              <Badge variant="outline">{result.editType}</Badge>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Created At:</span>
            <span className="text-sm">{formatTimestamp(result.createdAt)}</span>
          </div>
          {result.errorMessage && (
            <div>
              <span className="text-sm text-muted-foreground">Error:</span>
              <pre className="mt-1 overflow-auto whitespace-pre-wrap break-words rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {result.errorMessage}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Init Timing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard label="Duration" value={formatDurationMs(result.totalDurationMs)} />
            <MetricCard label="WASM Load" value={formatDurationMs(result.wasmLoadDurationMs)} />
            <MetricCard label="WASM Init" value={formatDurationMs(result.wasmInitDurationMs)} />
            <MetricCard
              label="Time To First Frame"
              value={formatDurationMs(result.timeToFirstFrameMs)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Network</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard label="Observed Mbps" value={formatMbps(result.networkObservedMbps)} />
            <MetricCard label="Browser Downlink" value={formatMbps(result.networkDownlinkMbps)} />
            <MetricCard label="Effective Type" value={result.networkEffectiveType ?? "-"} />
            <MetricCard
              label="RTT"
              value={result.networkRttMs != null ? `${result.networkRttMs} ms` : "-"}
            />
            <MetricCard label="Save Data" value={formatYesNo(result.networkSaveData)} />
            <MetricCard label="Protocol" value={result.httpProtocolMajority ?? "-"} />
            <MetricCard
              label="Visible Throughout"
              value={formatYesNo(result.wasVisibleThroughout)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard label="Project Assets" value={result.projectAssetCount ?? "-"} />
            <MetricCard label="Resources Touched" value={result.assetCount} />
            <MetricCard label="Cache Hits" value={result.assetCacheHitCount} />
            <MetricCard label="Revalidated" value={result.assetRevalidatedCount} />
            <MetricCard label="Cache Misses" value={result.assetMissCount} />
            <MetricCard label="Max Concurrent" value={result.maxConcurrentFetches} />
            <MetricCard label="From Network" value={formatBytes(result.assetBytesFromNetwork)} />
            <MetricCard label="From Cache" value={formatBytes(result.assetBytesFromCache)} />
            <MetricCard label="Total Bytes" value={formatBytes(totalBytes)} />
          </div>
          {result.assets && <AssetTable assets={result.assets} />}
        </CardContent>
      </Card>
    </div>
  );
}
